import React, {FC, useState} from "react";
import {Box, styled, Tooltip} from "@mui/material";
import {Accordion as MuiAccordion, AccordionDetails, AccordionSummary, TextField} from "@material-ui/core";
import {ExpandMore as ExpandMoreIcon} from "@material-ui/icons";
import {useTranslation} from "react-i18next";
import {useSelector} from "../redux/store";
import i18n from "../translations/i18n";
import {FLAGS} from "./LanguagesDropdown";
import {Languages} from "../constants/languages";
import MultiLanguageInput from "./MultiLanguageInput";
import {removeSuffix} from "../utils/functions";

const Accordion = styled(MuiAccordion)`
  border-radius: 10px !important;
  width: 100%;
  background: unset;
  .MuiAccordionSummary-root {
    padding: 0;
  }
  .MuiAccordionSummary-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0;
  }
  .MuiAccordionDetails-root {
    padding: 0;
  }
  .Mui-expanded {
    background-color: unset;
    border-radius: 10px 10px 0 0;
  }
  .Mui-focused {
    background-color: unset;
  }
  .MuiAccordionSummary-expandIcon {
    position: absolute;
    right: 44px;
    top: 3px;
  }
  &::before {
    background-color: unset;
  }
`

const InputWrapper = styled('div')`
  position: relative;
  width: 100%;
`

const Flag = styled('div')`
  position: absolute;
  z-index: 5;
  top: 15px;
  right: 11px
`


type PropsType = {
  onChange: (text: string, language: string) => void
  value: {[key: string]: string}
  helperText?: string
  variant?: "filled" | "outlined" | "standard" | undefined
  label?: string
  placeholder?: string
  error?: boolean
}

const AccordionMultiLanguageInputs: FC<PropsType> = ({
  value,
  onChange,
  helperText,
  variant = 'outlined',
  label,
  placeholder,
  error = false
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const company = useSelector((state) => state.companyReducer.company);

  const namesLanguages: { [key: string]: any } = {
    [Languages.English]: t("LanguagesDropdown_English"),
    [Languages.Swedish]: t("LanguagesDropdown_Swedish"),
    [Languages.Danish]: t("LanguagesDropdown_Danish"),
    [Languages.German]: t("LanguagesDropdown_German"),
    [Languages.Spanish]: t('LanguagesDropdown_Spanish'),
    [Languages.French]: t("LanguagesDropdown_French"),
    [Languages.Norwegian]: t("LanguagesDropdown_Norwegian"),
    [Languages.Portuguese]: t("LanguagesDropdown_Portuguese"),
    [Languages.Ukrainian]: t("LanguagesDropdown_Ukrainian"),
  }

  const getIsoLanguageForInput = () => {
    const language = removeSuffix(i18n.language)
    const result: {current: string, optional: string[]} = {
      current: 'en',
      optional: []
    }
    if (company && company.supported_languages) {
      if (company.supported_languages.includes(language)) {
        result.current = language
      }
      result.optional = company.supported_languages.filter((i) => i !== result.current)
    }
    return result
  }

  return (
    <Accordion expanded={expanded}>
      <AccordionSummary
        expandIcon={
          <Tooltip title={`${expanded ? 'Hide' : 'Show'} other languages`} placement={'top'} arrow>
            <ExpandMoreIcon />
          </Tooltip>
        }
        aria-controls={`multi-language-input-content`}
        id={`multi-language-input-header`}
        IconButtonProps={{
          onClick: () => setExpanded(!expanded)
        }}
      >
        <InputWrapper>
          <TextField
            fullWidth
            value={value[getIsoLanguageForInput().current] ? value[getIsoLanguageForInput().current] : ''}
            label={label ? `${namesLanguages[getIsoLanguageForInput().current]} ${t(label).toLowerCase()}` : ''}
            placeholder={placeholder}
            helperText={helperText}
            variant={variant}
            onChange={(e) => onChange(e.target.value, getIsoLanguageForInput().current)}
            error={error}
          />
          <Tooltip title={`This is ${namesLanguages[getIsoLanguageForInput().current]} language`} placement={'top'} arrow>
            <Flag>
              {FLAGS[getIsoLanguageForInput().current]}
            </Flag>
          </Tooltip>
        </InputWrapper>
      </AccordionSummary>
      <AccordionDetails>
        <Box display='flex' flexDirection='column' width={'100%'}>
          {company && company.supported_languages.length > 0 ? (
            <>
              {getIsoLanguageForInput().optional.map((lang, index) => (
                <Box mt={2} key={index}>
                  <MultiLanguageInput
                    variant={variant}
                    label={label}
                    placeholder={placeholder}
                    lang={lang}
                    value={value}
                    onChange={onChange}
                  />
                </Box>
              ))}
            </>
          ) : null}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default AccordionMultiLanguageInputs